/**
 * Resource Alerts HTTP layer. All paths under
 * /organizations/{org_id}/alerts/resource. Shared apiGet/apiPut/apiPost
 * apply baseURL (/api/v1) + auth + envelope unwrap.
 */
import { apiGet, apiPut, apiPatch, apiPost } from "@shared/api";
import {
  alertListSchema, alertDetailSchema, alertSummarySchema,
  ackOneSchema, ackBulkSchema,
  configSchema, saveConfigSchema, toggleConfigSchema, resetConfigSchema,
  type AlertList, type AlertDetail, type AlertSummary,
  type AckOne, type AckBulk,
  type Config, type SaveConfig, type ToggleConfig, type ResetConfig,
  type AlertsQuery, type AlertType, type Rule,
} from "./resourceAlerts.schemas";

const enc = (s: string): string => encodeURIComponent(s);
const base = (orgId: string): string => `/organizations/${enc(orgId)}/alerts/resource`;

/** Serialise list filters. Empty values dropped; ≥2-char search enforced. */
function serialiseQuery(q: AlertsQuery): string {
  const p = new URLSearchParams();
  for (const s of q.state ?? [])      if (s) p.append("state", s);
  for (const t of q.alert_type ?? []) if (t) p.append("alert_type", t);
  for (const s of q.severity ?? [])   if (s) p.append("severity", s);
  if (q.agent_id)                     p.set("agent_id", q.agent_id);
  if (q.acknowledged !== undefined)   p.set("acknowledged", String(q.acknowledged));
  if (q.search && q.search.trim().length >= 2) p.set("search", q.search.trim());
  p.set("page",     String(q.page ?? 1));
  p.set("per_page", String(q.per_page ?? 50));
  const s = p.toString();
  return s ? `?${s}` : "";
}

/* reads */
export const getAlerts = (orgId: string, q: AlertsQuery = {}): Promise<AlertList> =>
  apiGet(alertListSchema, `${base(orgId)}${serialiseQuery(q)}`);

export const getAlertDetail = (orgId: string, alertId: string): Promise<AlertDetail> =>
  apiGet(alertDetailSchema, `${base(orgId)}/${enc(alertId)}`);

export const getAlertSummary = (orgId: string): Promise<AlertSummary> =>
  apiGet(alertSummarySchema, `${base(orgId)}/summary`);

/* acknowledge */
export const acknowledgeAlert = (orgId: string, alertId: string): Promise<AckOne> =>
  apiPost(ackOneSchema, `${base(orgId)}/${enc(alertId)}/acknowledge`, {});

export const acknowledgeBulk = (orgId: string, alertIds: string[]): Promise<AckBulk> =>
  apiPost(ackBulkSchema, `${base(orgId)}/acknowledge`, { alert_ids: alertIds });

/* config */
export const getConfig = (orgId: string): Promise<Config> =>
  apiGet(configSchema, `${base(orgId)}/config`);

export const saveConfig = (orgId: string, rules: Record<string, Partial<Rule>>): Promise<SaveConfig> =>
  apiPut(saveConfigSchema, `${base(orgId)}/config`, { rules });

export const toggleConfig = (orgId: string, alertType: AlertType, patch: Partial<Rule>): Promise<ToggleConfig> =>
  apiPatch(toggleConfigSchema, `${base(orgId)}/config/${enc(alertType)}`, patch);

export const resetConfig = (orgId: string): Promise<ResetConfig> =>
  apiPost(resetConfigSchema, `${base(orgId)}/config/reset`, {});
